import type { MessageInputMethod } from "@/analytics/surfaces";
import { useFlowSessionById } from "@/api/flow_sessions";
import { useFlowMessages } from "@/api/messages";
import { MessageInput } from "@/components/MessageInput";
import SessionChatArea from "@/components/SessionChatArea";
import { MessageInputControlSet } from "@/constants/message-input";
import { MessageInputProvider } from "@/context/MessageInputContext";
import { SessionProvider } from "@/context/SessionContext";
import { DropZoneProvider, useOnDrop } from "@/dnd/OsFileDropLayer";
import { useMessageActions } from "@/hooks/useMessageActions";
import { cn } from "@/utils";

import { ReferencedEntitiesPills } from "@/components/ReferencedEntitiesPills";
import { useFileRecordTextSelectionReferencedEntity } from "@/panes/file-record-detail/useFileRecordTextSelectionReferencedEntity";
import { mergeCanvasReferencedEntities } from "@/utils/mergeReferencedEntities";
import type { PostMessageParams } from "@cohesive/api-client";
import { ChevronLeft, Maximize2, MessageSquare, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useCanvasSelectionMirror } from "../context/CanvasSelectionMirrorContext";
import { useCanvasReasoningEffort } from "../hooks/useCanvasReasoningEffort";
import { convertNodesToReferencedEntities } from "../utils/convertNodesToReferencedEntities";

// Exported so ChatSessionNode can use them for ViewportPortal sizing
export const PANEL_WIDTH = 420;
export const PANEL_HEIGHT = 520;

/**
 * The ComposerShell provides the framed surface (1.5px border + rust focus
 * ring) inside the floating panel; this config only compacts the editor for
 * the narrow popover.
 */
const CANVAS_FLOATING_MESSAGE_INPUT_CLASSNAMES = {
  container: "w-full sm:max-w-none mx-0",
  contentWrapper: "",
  editor: "min-h-[20px] max-h-[96px] text-sm leading-5",
  placeholder: "text-sm leading-5",
} as const;

interface ChatPanelProps {
  flowSessionId: string;
  canvasId: string;
  /** Called synchronously at mousedown so caller can snapshot its start position */
  onDragHeaderStart: () => void;
  /** Called on every mousemove with cumulative (dx, dy) from the drag-start point */
  onDragHeader: (cumulativeDx: number, cumulativeDy: number) => void;
  onClose: () => void;
  onDelete?: () => void;
}

// ---------------------------------------------------------------------------
// Inner component — data / providers
// ---------------------------------------------------------------------------

function ChatPanelInner({
  flowSessionId,
  canvasId,
}: {
  flowSessionId: string;
  canvasId: string;
}) {
  const selectedNodes = useCanvasSelectionMirror();

  const {
    textSelectionEntities,
    removeSelection,
    clearSelectionsFromMessage: clearTextSelectionsFromMessage,
    isTextSelectionEntityId,
  } = useFileRecordTextSelectionReferencedEntity(canvasId);

  const { data: messages = [], refetch: refetchMessages } = useFlowMessages(
    flowSessionId,
    undefined,
    { enabled: !!flowSessionId },
  );

  const allReferencedEntities = useMemo(
    () => convertNodesToReferencedEntities(selectedNodes),
    [selectedNodes],
  );

  const [removedEntityIds, setRemovedEntityIds] = useState<Set<string>>(
    new Set(),
  );

  const selectedNodeIds = useMemo(
    () => selectedNodes.map((n) => n.id).join(","),
    [selectedNodes],
  );
  useEffect(() => {
    setRemovedEntityIds(new Set());
  }, [selectedNodeIds]);

  const referencedEntities = useMemo(
    () =>
      mergeCanvasReferencedEntities(
        allReferencedEntities,
        textSelectionEntities,
        removedEntityIds,
      ),
    [allReferencedEntities, removedEntityIds, textSelectionEntities],
  );

  const handleRemoveEntity = useCallback(
    (entityId: string) => {
      if (isTextSelectionEntityId(entityId)) {
        removeSelection(entityId);
        return;
      }
      setRemovedEntityIds((prev) => new Set([...prev, entityId]));
    },
    [removeSelection, isTextSelectionEntityId],
  );

  const [reasoningEffort, setReasoningEffort] =
    useCanvasReasoningEffort(canvasId);

  const { data: flowSession } = useFlowSessionById(flowSessionId, {
    enabled: !!flowSessionId,
  });
  const isSendDisabled = flowSession
    ? flowSession.status !== "ready" && flowSession.status !== "initialized"
    : false;

  // Files attached in canvas-scoped chat are flow-session-scoped (like
  // standalone chat) — they ride on the message via `file_record_ids`
  // but don't auto-spawn a Loro node on the canvas. Users who want a
  // file ON the canvas drop it directly there via the canvas drop
  // target / paste / quick-add surfaces.
  const {
    onSendMessage: onSendMessageBase,
    isLoading,
    uploadedFiles,
    onAttachFile,
    onAttachFileRecords,
    onRemoveFile,
  } = useMessageActions({
    surface: "canvas_quick_chat",
    onMessageSent: () => {
      setTimeout(() => refetchMessages(), 500);
    },
  });

  // Own the chat drop target so dropped files land on this hook's upload
  // queue (the visible composer), not the hidden ChatArea instance.
  const onOsFileDrop = useCallback(
    (files: File[] | null) => onAttachFile(files, { method: "drop_os" }),
    [onAttachFile],
  );
  useOnDrop(onOsFileDrop);

  const onSendMessage = useCallback(
    async (
      params: PostMessageParams,
      meta?: { inputMethod: MessageInputMethod },
    ) => {
      await onSendMessageBase(params, meta);
      clearTextSelectionsFromMessage(params.referenced_entities);
    },
    [onSendMessageBase, clearTextSelectionsFromMessage],
  );

  const hasMessages = messages.length > 0;

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {hasMessages ? (
        <div
          className="flex-1 min-h-0 overflow-hidden px-3"
          style={{ zoom: 0.9 }}
        >
          {/* ChatArea messages only — composer is the MessageInput below. */}
          <SessionChatArea
            surface="canvas_quick_chat"
            location="sidebar"
            placeholder="What would you like me to do?"
            className="!h-full"
            controlSet={MessageInputControlSet.CANVAS_QUICK_CHAT}
            hideMessageInput={true}
            referencedEntities={referencedEntities}
            onSendSuccess={clearTextSelectionsFromMessage}
          />
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
          <p className="text-sm text-muted-foreground">
            Start chatting with your canvas agent.
          </p>
        </div>
      )}

      <MessageInput
        flowSessionId={flowSessionId}
        // Canvas-class sessions carry no workspace_id — the canvas is how
        // MessageInput resolves the workspace that leads the picker.
        canvasId={canvasId}
        onSend={onSendMessage}
        referencedEntities={referencedEntities}
        attachedFiles={uploadedFiles}
        onAttachFile={onAttachFile}
        onAttachFileRecords={onAttachFileRecords}
        onRemoveFile={onRemoveFile}
        sendButtonDisabled={isSendDisabled || isLoading}
        controlSet={MessageInputControlSet.CANVAS_QUICK_CHAT}
        compactAgentLine
        location="sidebar"
        placeholder="What would you like me to do?"
        autoFocus={!hasMessages}
        className="shrink-0 px-3 pb-3"
        classNames={CANVAS_FLOATING_MESSAGE_INPUT_CLASSNAMES}
        agentReasoningEffort={reasoningEffort}
        onAgentReasoningEffortChange={setReasoningEffort}
        middleToolbarSlot={
          referencedEntities.length > 0 ? (
            <ReferencedEntitiesPills
              entities={referencedEntities}
              onRemove={handleRemoveEntity}
              className="px-0 py-0"
            />
          ) : null
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Outer panel shell — positioned via ViewportPortal in ChatSessionNode
// ---------------------------------------------------------------------------

export function ChatPanel({
  flowSessionId,
  canvasId,
  onDragHeaderStart,
  onDragHeader,
  onClose,
  onDelete,
}: ChatPanelProps) {
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.closest("button")) return;

      e.preventDefault();
      e.stopPropagation();

      onDragHeaderStart();

      const startX = e.clientX;
      const startY = e.clientY;

      const onMove = (ev: MouseEvent) => {
        onDragHeader(ev.clientX - startX, ev.clientY - startY);
      };

      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [onDragHeaderStart, onDragHeader],
  );

  const handlePopOut = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent("canvas:open-session-pane", {
        detail: { flowSessionId, canvasId },
      }),
    );
    onClose();
  }, [flowSessionId, canvasId, onClose]);

  return (
    <div
      className={cn(
        // Design-system panel chrome: rounded-xl + hairline edge + elev-3.
        // Solid bg-background to match the FAB main-chat surface
        // (CanvasSessionPaneContent) — bg-card/95 read noticeably lighter.
        "bg-surface-floating-chat border border-n-10 rounded-xl shadow-elev-3",
        "flex flex-col overflow-hidden h-full w-full",
      )}
      // Block all canvas interactions from passing through the panel
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2.5 border-b dark:border-neutral-800/60 cursor-grab active:cursor-grabbing select-none shrink-0"
        onMouseDown={handleDragStart}
      >
        <div className="flex items-center gap-2.5">
          <MessageSquare className="h-4 w-4 text-foreground" strokeWidth={2} />
          <span className="text-sm font-semibold text-foreground">
            Quick Chat
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="h-7 w-7 flex items-center justify-center rounded-sm text-foreground hover:bg-accent dark:hover:bg-n-95/10 transition-colors"
            title="Minimize"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handlePopOut();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="h-7 w-7 flex items-center justify-center rounded-sm text-foreground hover:bg-accent dark:hover:bg-n-95/10 transition-colors"
            title="Open in side pane"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              className="h-7 w-7 flex items-center justify-center rounded-sm text-lc-blocked hover:bg-lc-blocked/10 transition-colors"
              title="Archive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* MessageInputProvider required by ChatArea (LexicalComposerContext) */}
      <DropZoneProvider
        key={flowSessionId}
        className="flex-1 min-h-0 flex flex-col"
      >
        <MessageInputProvider>
          <SessionProvider initialSessionId={flowSessionId}>
            <ChatPanelInner flowSessionId={flowSessionId} canvasId={canvasId} />
          </SessionProvider>
        </MessageInputProvider>
      </DropZoneProvider>
    </div>
  );
}
